import { Loader2, MessageSquare, Star, X } from 'lucide-react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { submitFeedback } from '../services/feedbackService';
import { FeedbackType } from '../types';

const FEEDBACK_TYPES: { value: FeedbackType; label: string; emoji: string }[] = [
  { value: 'bug', label: 'Bug', emoji: '🐛' },
  { value: 'feature', label: 'Feature', emoji: '💡' },
  { value: 'general', label: 'General', emoji: '💬' },
  { value: 'praise', label: 'Praise', emoji: '🙌' },
];

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

const FeedbackButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('general');
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const location = useLocation();

  const resetForm = () => {
    setType('general');
    setRating(null);
    setMessage('');
    setSubmitState('idle');
  };

  const handleClose = () => {
    setIsOpen(false);
    if (submitState === 'success') resetForm();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitState('submitting');

    try {
      submitFeedback(
        type,
        message.trim(),
        location.pathname,
        rating as 1 | 2 | 3 | 4 | 5 | undefined
      );
      setSubmitState('success');
      setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 2000);
    } catch {
      setSubmitState('error');
    }
  };

  const handleStarClick = (star: number) => {
    setRating(rating === star ? null : star);
  };

  const pageName = location.pathname === '/' ? 'Dashboard' : location.pathname.replace(/^\//, '').replace(/-/g, ' ');

  return (
    <>
      {/* Vertical tab button on right edge */}
      <button
        onClick={() => { setIsOpen(true); setSubmitState('idle'); }}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-orange-500 hover:bg-orange-600 text-white px-2 py-4 rounded-l-lg shadow-lg transition-colors flex flex-col items-center gap-2"
        aria-label="Send feedback"
      >
        <MessageSquare size={18} />
        <span className="text-xs font-semibold tracking-wider" style={{ writingMode: 'vertical-rl' }}>
          Feedback
        </span>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-sage-900">Send Feedback</h2>
                  <p className="text-sm text-sage-500 capitalize">Page: {pageName}</p>
                </div>
                <button
                  onClick={handleClose}
                  className="text-sage-600 hover:text-sage-900 p-1"
                  aria-label="Close feedback form"
                >
                  <X size={20} />
                </button>
              </div>

              {submitState === 'success' ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">🎉</div>
                  <p className="text-lg font-semibold text-sage-900">Thank you!</p>
                  <p className="text-sage-600 text-sm">Your feedback has been submitted.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Type selector */}
                  <div>
                    <label className="block text-sm font-medium text-sage-700 mb-2">Type</label>
                    <div className="grid grid-cols-4 gap-2">
                      {FEEDBACK_TYPES.map((ft) => (
                        <button
                          key={ft.value}
                          type="button"
                          onClick={() => setType(ft.value)}
                          className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-sm transition-colors ${
                            type === ft.value
                              ? 'border-orange-400 bg-orange-50 text-orange-700'
                              : 'border-sage-200 text-sage-600 hover:border-sage-300'
                          }`}
                        >
                          <span>{ft.emoji}</span>
                          <span className="font-medium">{ft.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Star rating */}
                  <div>
                    <label className="block text-sm font-medium text-sage-700 mb-2">Rating (optional)</label>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleStarClick(star)}
                          className="p-1 transition-colors"
                          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                        >
                          <Star
                            size={24}
                            className={
                              rating && star <= rating
                                ? 'fill-orange-400 text-orange-400'
                                : 'text-sage-300'
                            }
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-sage-700 mb-2">Message</label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                      placeholder="Tell us what's on your mind..."
                      rows={4}
                      required
                      className="w-full border border-sage-300 rounded-lg p-3 text-sage-900 placeholder-sage-400 focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none resize-none"
                    />
                    <p className="text-xs text-sage-400 text-right mt-1">{message.length}/1000</p>
                  </div>

                  {submitState === 'error' && (
                    <p className="text-red-600 text-sm">Something went wrong. Please try again.</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={!message.trim() || submitState === 'submitting'}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-sage-300 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {submitState === 'submitting' ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Feedback'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButton;
